// The profile's books half (docs/plans/people.md): a person as the books hold them — contact, emergency, pay, payroll,
// access, employment — read with `getPerson` when a profile opens, online. Privacy (SYNC.md §3.9): none of it enters
// the local store; it lives in this module's memory for the session and is dropped on sign-out or a switch of user.
// Every save is a books callable (they check admin-or-self themselves), then the person is read again.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { liveEarned, myShareOf, sharesInRange, computeCardActiveTime } from '@battleops/shared';
import { useSession } from '../context/SessionContext';
import { useLocalCollection, useLocalDoc } from './useLocal';
import { call } from '../session/session';

const cache = new Map();          // uid → person, this session only
let cacheOwner = null;
export function clearPersonCache() { cache.clear(); cacheOwner = null; }

/** → { person, loading, error, offline, reload } */
export function usePerson(uid) {
  const { identity, online } = useSession();
  if (cacheOwner !== (identity?.uid || null)) { cache.clear(); cacheOwner = identity?.uid || null; }
  const [person, setPerson] = useState(() => (uid ? cache.get(uid) || null : null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    if (!uid || !online) return null;
    setLoading(true); setError('');
    try {
      const p = await call('getPerson', { uid });
      cache.set(uid, p);
      setPerson(p);
      return p;
    } catch (e) { setError(String(e?.message || e)); return null; } finally { setLoading(false); }
  }, [uid, online]);
  useEffect(() => { setPerson(uid ? cache.get(uid) || null : null); if (uid && online) reload(); }, [uid, online]);
  return { person, loading, error, offline: !online && !person, reload };
}

/** The bill rate by role table (opsRates/billRoles — admins only; others get nothing). */
export function useBillRoles() {
  const doc = useLocalDoc('rates', 'billRoles');
  return Array.isArray(doc?.roles) ? doc.roles : [];
}

const held = (r) => !!(r?.pendingApproval || r?.held);
async function save(name, data) {
  const r = await call(name, data);
  if (held(r)) { const e = new Error('Waiting for approval in Bmail → Approvals.'); e.held = true; throw e; }
  return r;
}

// Saves. Each returns the callable's answer; the screen reloads the person after.
export const personApi = {
  /** Contact, emergency contact, legal name (admin or self). */
  savePrivate: (uid, patch) => save('updatePersonPrivate', { uid, ...patch }),
  /** Name / title (admin or self). */
  saveProfile: (uid, patch) => save('updatePersonProfile', { uid, ...patch }),
  /** Role, departments, apps, BattleOps grants (admin). */
  saveMember: (uid, patch) => save('updateTeamMember', { uid, ...patch }),
  /** The one pay save (admin): payType, rate, contractorPay, shadow, tradeRole, sharePct, standard hours, vacation %. */
  savePay: (uid, pay) => save('savePersonPay', { uid, ...pay }),
  /** Status (active / on leave) + dates (admin). */
  saveEmployment: (uid, patch) => save('saveEmployment', { uid, ...patch }),
  /** Lay off / end (admin) — signs them out everywhere. */
  endEmployment: (uid, input) => save('endEmployment', { uid, ...input }),
  undoEndEmployment: (uid) => save('undoEndEmployment', { uid }),
  billRoles: () => call('getBillRoles', {}).then((r) => r?.roles || []),
  saveBillRoles: (roles) => save('saveBillRoles', { roles }),
  /** The person's own payslips (any worker, their own). */
  myPayslips: () => call('getMyPayslips', {}).then((r) => r?.slips || []),
  myPayslipPdf: (runId) => call('getMyPayslipPdf', { runId }),
  /** Tick off what they signed before day one (the hire's checklist): { key: true | false } (admin). */
  tickAgreements: (uid, ticks) => save('saveEmployment', { uid, agreements: ticks }),
  // ---- hire (people.md P6) — shared/hire.js hirePayload() is the input ----
  /** Send the invite: the books stage the whole hire and apply it when they accept. */
  hire: (payload) => save('createTeamInvite', payload),
  /** Create now (no invite; the email may be blank — then no password login): the hire applied at once. */
  createNow: (payload) => save('createTeamMember', payload),
  invites: () => call('listTeamInvites', {}).then((r) => r?.invites || []),
  cancelInvite: (token) => save('cancelTeamInvite', { token }),
};

// ---- share pay (people.md P7) -----------------------------------------------------------------------------------
// The books publish each person's share of each task to THEIR job-pay doc (opsJobPay/{projectId}__{uid}.share), which
// the server sends only to that person and admins (SYNC.md §3.9) — so this reads the store, offline, and never sees a
// task's labour value or anyone else's share unless the viewer is an admin.

/** Job-pay docs carrying a share, for one person (yours, or anyone's for an admin). */
export function useShareDocs(uid) {
  const all = useLocalCollection('jobPay');
  return useMemo(() => all.filter((j) => j && j.uid === uid && j.share), [all, uid]);
}

/** What this task is worth to `uid`, live: the published share + their running timer on it. → { amount, live, done } | null */
export function useEarned(task, uid, { timer = null, now = Date.now() } = {}) {
  const docs = useShareDocs(uid);
  const pub = task && uid ? myShareOf(docs, task.projectId, uid, task.id) : null;
  const running = timer && timer.taskId === task?.id ? computeCardActiveTime(timer, now) : 0;
  return pub ? liveEarned(pub, running) : null;
}

/** The Pay tab's week: shares on tasks finished in [from, to), and what's building on open ones. */
export function useWeekShares(uid, from, to) {
  const docs = useShareDocs(uid);
  return useMemo(() => sharesInRange(docs, { from, to }), [docs, from, to]);
}
